import {
  viewChild,
  Component,
  computed,
  DestroyRef,
  OnInit,
  Signal,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { features } from '@consts/text.const';
import { SharedEffects } from '@effects/shared.effects';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PreloaderColor } from '@enums/shared.enum';
import { syncQueryParam } from 'src/app/helpers/sync-query-param.helper';
import { WorkflowWebhooksSelectors } from '@states/workflow-webhooks/workflow-webhooks.selector-types';
import { WorkflowWebhookActions } from '@states/workflow-webhooks/workflow-webhooks.action-type';
import { ActiveOrganization } from '@models/organization.model';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { WebhookDialogComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-webhook/components/webhook-dialog/webhook-dialog.component';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { UiSearchComponent } from 'src/app/shared/ui-kit/ui-search/ui-search.component';
import { RouteModalService, UiButtonComponent, UiPreloaderComponent, UiNoDataComponent } from '@ui';
import { SharedActions } from '@states/shared/shared.action-types';
import { ConfirmDialogModel } from '@ui';
import { ConfirmModalModel } from 'src/app/services/confirm-dialogs/confirm-modal.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ofType } from '@ngrx/effects';
import { filter, tap } from 'rxjs';
import { WorkflowWebhookModel } from '@models/workflow-webhook.model';
import { MenuActions } from '@states/menu/menu.action-types';
import { MenuKey } from '@enums/menu.enum';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { Dictionary } from '@ngrx/entity/src';
import { WorkflowModel } from '@models/workflow.model';
import { WorkflowActions } from '@states/workflow/workflow.action.types';
import { InputDialog } from 'src/app/shared/flow-chart/components/input-modal/input-dialog';
import { SharedModels } from '@share-models';
import { CellTemplateContext, DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createWebhooksTableConfig } from './webhooks-table.util';
import { Router } from '@angular/router';
import { routerSegments } from '@consts/routes';

@Component({
  selector: 'app-webhooks',
  imports: [
    UiSearchComponent,
    UiButtonComponent,
    TranslatePipe,
    UiPreloaderComponent,
    UiNoDataComponent,
    UiDatasetTableComponent,
  ],
  templateUrl: './webhooks.component.html',
  styleUrl: './webhooks.component.scss',
})
export class WebhooksComponent implements OnInit {
  public loaderColor = PreloaderColor;
  public noResults = features.tags.noResults;
  public noData = features.webhooks.noData;
  public viewStates = SharedModels.ViewState;

  protected search: WritableSignal<string> = syncQueryParam<string>('search');

  public selectWebhooksSig: Signal<
    WorkflowWebhookModel.WebhookEndpointMongoDocument[]
  > = this.store$.selectSignal(
    WorkflowWebhooksSelectors.selectWorkflowWebhooks,
  );
  public selectIsLoadingSig: Signal<boolean> = this.store$.selectSignal(
    WorkflowWebhooksSelectors.selectIsLoading,
  );
  public selectOrganizationSig: Signal<ActiveOrganization> =
    this.store$.selectSignal(OrganizationSelectors.selectActiveOrganization);
  public selectWorkflowsEntitiesSig: Signal<Dictionary<WorkflowModel.WorkflowDocument>> = this.store$.selectSignal(
    WorkflowsSelectors.selectWorkflowsEntities,
  );
  public readonly connectedGraphCellTemplate = viewChild.required<
    TemplateRef<CellTemplateContext<WorkflowWebhookModel.WebhookEndpointMongoDocument>>
  >('connectedGraphCell');

  public filteredWebhooksSig: Signal<
    WorkflowWebhookModel.WebhookEndpointMongoDocument[]
  > = computed(() => {
    const webhooks = this.selectWebhooksSig();
    const query = this.search()?.toLowerCase();

    return query
      ? webhooks.filter((webhook) => (webhook.name ?? '').toLowerCase().includes(query))
      : webhooks;
  });

  public tableConfig: Signal<DatasetTableConfig<WorkflowWebhookModel.WebhookEndpointMongoDocument>> =
    computed(() => createWebhooksTableConfig({
      templates: {
        connectedGraphCell: this.connectedGraphCellTemplate(),
      },
    }));

  public viewState: Signal<SharedModels.ViewState> = computed(() => {
    if (this.filteredWebhooksSig().length > 0) {
      return SharedModels.ViewState.HAS_CONTENT;
    }

    return this.search()
      ? SharedModels.ViewState.NO_RESULTS
      : SharedModels.ViewState.INITIAL_EMPTY;
  });

  constructor(
    private store$: Store,
    private sharedEffects$: SharedEffects,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
    private translateService: TranslateService,
    private modalService: RouteModalService,
    private router: Router,
  ) { }

  public ngOnInit(): void {
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.webhooks }));

    const orgId = this.selectOrganizationSig().orgId as string;

    this.store$.dispatch(WorkflowActions.getWorkflows({
      orgId: orgId
    }));

    this.store$.dispatch(
      WorkflowWebhookActions.getWorkflowWebhooks({ orgId: orgId }),
    );

    this.sharedEffects$.confirmation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(SharedActions.showConfirmModalResultConfirm),
        filter(
          (x) =>
            x.params['type'] === ConfirmModalModel.Action.removeWorkflowWebhook,
        ),
        tap((x) => {
          this.store$.dispatch(
            WorkflowWebhookActions.deleteWorkflowWebhook({
              id: x.params['id'],
            }),
          );
        }),
      )
      .subscribe();

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.createWorkflowWebhook,
          open: () => this.openCreateDialog(),
        },
        {
          queryParam: openDialogByParams.editWorkflowWebhook,
          open: ({ webhookId }: Record<string, string>) => this.openEditDialog(webhookId),
          onClose: () => {
            this.modalService.removeQueryParam([
              openDialogByParams.editWorkflowWebhook,
              'webhookId',
            ]);
          },
        },
      ],
      this.destroyRef,
    );
  }

  public openCreateDialog(): MatDialogRef<WebhookDialogComponent, unknown> {
    return this.dialog.open(WebhookDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '760px',
      maxHeight: '80vh',
    });
  }

  public openEditDialog(id: string): MatDialogRef<WebhookDialogComponent, unknown> {
    return this.dialog.open(WebhookDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '760px',
      maxHeight: '80vh',
      data: {
        webhookId: id,
      },
    });
  }

  public edit(webhookId: string): void {
    this.modalService.openModal(openDialogByParams.editWorkflowWebhook, {
      webhookId: webhookId,
    });
  }

  public delete(id: string, name: string): void {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          title: this.translateService.instant(
            'DATABASE_TAGS.DELETE_MODAL.TITLE',
          ),
          msg: this.translateService.instant(
            'DATABASE_TAGS.DELETE_MODAL.MESSAGE',
            { name: `"${name}"` },
          ),
          confirm: this.translateService.instant('COMMON.DELETE'),
          disableClose: true,
          params: {
            type: ConfirmModalModel.Action.removeWorkflowWebhook,
            id: id,
          },
        },
      }),
    );
  }

  public create(): void {
    this.modalService.openModal(openDialogByParams.createWorkflowWebhook);
  }

  public runWorkflow(nodeId: string, graphId: string): void {
    const orgId = this.selectOrganizationSig().orgId as string;

    this.dialog
      .open(InputDialog, {
        panelClass: 'modal-no-padding',
        maxHeight: '80vh',
        width: '1048px',
      })
      .afterClosed()
      .subscribe((dialogData) => {
        if (dialogData) {
          const runDto = { startNodeId: nodeId, input: { message: dialogData } };

          this.store$.dispatch(
            WorkflowActions.runGraphWorkflow({
              orgId: orgId,
              graphId: graphId,
              runGraph: runDto,
            }),
          );
        }
      })
  }

  public goToGraph(graphId: string): void {
    this.router.navigate([routerSegments.workflows, graphId]);
  }

  public onRowAction(event: { actionId: string; row: WorkflowWebhookModel.WebhookEndpointMongoDocument }): void {
    if (event.actionId === 'edit') {
      this.edit(event.row._id);
      return;
    }
    if (event.actionId === 'delete') {
      this.delete(event.row._id, event.row.name ?? '');
      return;
    }
    if (event.actionId === 'run' && event.row.target?.graphId && event.row.target?.nodeId) {
      this.runWorkflow(event.row.target.nodeId, event.row.target.graphId);
    }
  }
}
